import React from 'react'
import type { AcademyLine, Course, Difficulty, QuizQuestion } from '../../../types/academy'

export interface DraftLesson {
  key: string
  title: string
  notes: string
  locked: boolean
}

export interface DraftModule {
  key: string
  title: string
  quizFocus: string
  quiz?: QuizQuestion[]
  locked: boolean
  lessons: DraftLesson[]
}

export interface DraftDetails {
  title: string
  serviceLine: AcademyLine
  difficulty: Difficulty
  hours: string
  sectors: string[]
  allSectors: boolean
  description: string
}

/**
 * Form state for building or editing a course.
 * Learner progress is stored by module and lesson position, so on a live course existing modules and lessons
 * can be renamed and rewritten but not removed; new ones are added at the end.
 */
export function useCourseBuilder(course: Course | undefined, live: boolean) {
  const [details, setDetails] = React.useState<DraftDetails>(() => ({
    title: course?.title ?? '',
    serviceLine: course?.serviceLine ?? 'ai',
    difficulty: course?.difficulty ?? 'Beginner',
    hours: course ? String(course.hours) : '6',
    sectors: course?.sectors ?? [],
    allSectors: course?.allSectors ?? false,
    description: course?.description ?? '',
  }))
  const [modules, setModules] = React.useState<DraftModule[]>(() =>
    course
      ? course.modules.map((m) => ({
          key: key(),
          title: m.title,
          quizFocus: m.quizFocus,
          quiz: m.quiz,
          locked: live,
          lessons: m.lessons.map((title, li) => ({ key: key(), title, notes: m.lessonNotes?.[li] ?? '', locked: live })),
        }))
      : [blankModule()],
  )
  const [note, setNote] = React.useState('')
  const [notify, setNotify] = React.useState(true)
  const [errors, setErrors] = React.useState<string[]>([])

  const setDetail = <K extends keyof DraftDetails>(field: K, value: DraftDetails[K]) => setDetails((d) => ({ ...d, [field]: value }))

  const updateModule = (mk: string, patch: Partial<DraftModule>) =>
    setModules((ms) => ms.map((m) => (m.key === mk ? { ...m, ...patch } : m)))
  const addModule = () => setModules((ms) => [...ms, blankModule()])
  const removeModule = (mk: string) => setModules((ms) => ms.filter((m) => m.key !== mk || m.locked))

  const updateLesson = (mk: string, lk: string, patch: Partial<DraftLesson>) =>
    setModules((ms) => ms.map((m) => (m.key === mk ? { ...m, lessons: m.lessons.map((l) => (l.key === lk ? { ...l, ...patch } : l)) } : m)))
  const addLesson = (mk: string) =>
    setModules((ms) => ms.map((m) => (m.key === mk ? { ...m, lessons: [...m.lessons, blankLesson()] } : m)))
  const removeLesson = (mk: string, lk: string) =>
    setModules((ms) => ms.map((m) => (m.key === mk ? { ...m, lessons: m.lessons.filter((l) => l.key !== lk || l.locked) } : m)))

  const validate = (): string[] => {
    const problems: string[] = []
    const hours = Number(details.hours)
    if (!details.title.trim()) problems.push('Give the course a title.')
    if (details.title.length > 80) problems.push('Keep the title to 80 characters.')
    if (wordCount(details.description) < 20) problems.push('Write a description of at least 20 words. The spec recommends 150 to 300.')
    if (!Number.isFinite(hours) || hours <= 0) problems.push('Estimated hours must be a positive number.')
    if (!details.allSectors && !details.sectors.length) problems.push('Choose at least one sector, or All sectors.')
    if (!modules.length) problems.push('Add at least one module.')
    const moduleTitles = new Set<string>()
    modules.forEach((m, mi) => {
      const label = `Module ${mi + 1}`
      if (!m.title.trim()) problems.push(`${label} needs a title.`)
      else if (moduleTitles.has(m.title.trim())) problems.push(`${label} has the same title as another module.`)
      moduleTitles.add(m.title.trim())
      if (!m.quizFocus.trim()) problems.push(`${label} needs a quiz focus.`)
      if (!m.lessons.length) problems.push(`${label} needs at least one lesson.`)
      const lessonTitles = new Set<string>()
      m.lessons.forEach((l, li) => {
        if (!l.title.trim()) problems.push(`${label}, lesson ${li + 1} needs a title.`)
        else if (lessonTitles.has(l.title.trim())) problems.push(`${label}, lesson ${li + 1} repeats another lesson title.`)
        lessonTitles.add(l.title.trim())
      })
    })
    setErrors(problems)
    return problems
  }

  const build = (): Omit<Course, 'slug' | 'number'> => ({
    title: details.title.trim(),
    serviceLine: details.serviceLine,
    difficulty: details.difficulty,
    hours: Number(details.hours),
    sectors: details.allSectors ? [] : details.sectors,
    allSectors: details.allSectors || undefined,
    description: details.description.trim(),
    flagship: course?.flagship,
    custom: course?.custom,
    modules: modules.map((m) => ({
      title: m.title.trim(),
      quizFocus: m.quizFocus.trim(),
      quiz: m.quiz,
      lessons: m.lessons.map((l) => l.title.trim()),
      lessonNotes: m.lessons.some((l) => l.notes.trim()) ? m.lessons.map((l) => l.notes.trim()) : undefined,
    })),
  })

  return {
    details,
    setDetail,
    modules,
    updateModule,
    addModule,
    removeModule,
    updateLesson,
    addLesson,
    removeLesson,
    note,
    setNote,
    notify,
    setNotify,
    errors,
    validate,
    build,
  }
}

export function wordCount(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}

function key(): string {
  return Math.random().toString(36).slice(2, 10)
}

function blankLesson(): DraftLesson {
  return { key: key(), title: '', notes: '', locked: false }
}

function blankModule(): DraftModule {
  return { key: key(), title: '', quizFocus: '', locked: false, lessons: [blankLesson()] }
}
